import "zod/compile";
import { oc } from "@orpc/contract";
import { z } from "zod";

export const PurchaseUploadIssueTypeEnum = z.enum([
  "missing_supplier_number",
  "missing_division",
  "invalid_amount",
  "negative_amount",
  "checksum_mismatch",
  "unmatched_supplier",
  "unmatched_division",
]);

export type PurchaseUploadIssueType = z.infer<
  typeof PurchaseUploadIssueTypeEnum
>;

export const PurchaseUploadIssueSeverityEnum = z.enum(["error", "warning"]);

export type PurchaseUploadIssueSeverity = z.infer<
  typeof PurchaseUploadIssueSeverityEnum
>;

export const PurchaseUploadIssueSchema = z.object({
  type: PurchaseUploadIssueTypeEnum,
  severity: PurchaseUploadIssueSeverityEnum,
  message: z.string(),
  rowNumber: z.number().nullable(),
  supplierCode: z.string().nullable(),
  supplierName: z.string().nullable(),
  divisionName: z.string().nullable(),
  month: z.number().int().min(1).max(12).nullable(),
  amount: z.number().nullable(),
});

export type PurchaseUploadIssue = z.infer<typeof PurchaseUploadIssueSchema>;

export const UnmatchedFileSupplierSchema = z.object({
  supplierCode: z.string(),
  supplierName: z.string(),
  rowCount: z.number(),
  currentYearYtd: z.number(),
});

export type UnmatchedFileSupplier = z.infer<typeof UnmatchedFileSupplierSchema>;

export const UnmatchedGentlySupplierSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  supplierCode: z.string().nullable(),
  reason: z.enum(["missing_corporate_supplier_number", "number_not_in_file"]),
});

export type UnmatchedGentlySupplier = z.infer<
  typeof UnmatchedGentlySupplierSchema
>;

export const PurchaseUnmatchedSummarySchema = z.object({
  inFileNotInGently: z.array(UnmatchedFileSupplierSchema),
  rebateSuppliersNotInFile: z.array(UnmatchedGentlySupplierSchema),
});

export type PurchaseUnmatchedSummary = z.infer<
  typeof PurchaseUnmatchedSummarySchema
>;

export const ParsePurchaseFileRequestSchema = z.object({
  xlsxBase64: z.string(),
  year: z.number().int().min(2000).max(2100),
  throughMonth: z.number().int().min(1).max(12).optional(),
  isPartialMonth: z.boolean().optional(),
  fileName: z.string().optional(),
});

export const ParsePurchaseFileResponseSchema = z.object({
  year: z.number().int(),
  throughMonth: z.number().int().min(1).max(12),
  isPartialMonth: z.boolean(),
  inferredThroughMonth: z.number().int().min(1).max(12),
  inferredIsPartialMonth: z.boolean(),
  detailRowCount: z.number(),
  storedRowCount: z.number(),
  currentYearTotal: z.number(),
  priorYearTotal: z.number(),
  uniqueSuppliers: z.number(),
  uniqueDivisions: z.number(),
  unmatched: PurchaseUnmatchedSummarySchema,
  matchedSupplierCount: z.number(),
  unmatchedDivisionNames: z.array(z.string()),
  validationIssues: z.array(PurchaseUploadIssueSchema),
});

export type ParsePurchaseFileRequest = z.infer<
  typeof ParsePurchaseFileRequestSchema
>;
export type ParsePurchaseFileResponse = z.infer<
  typeof ParsePurchaseFileResponseSchema
>;

export const ImportPurchaseFileRequestSchema = ParsePurchaseFileRequestSchema;

export const ImportPurchaseFileResponseSchema = z.object({
  success: z.boolean(),
  uploadId: z.string().uuid().nullable(),
  year: z.number().int(),
  throughMonth: z.number().int().min(1).max(12),
  isPartialMonth: z.boolean(),
  rowCount: z.number(),
  currentYearTotal: z.number(),
  priorYearTotal: z.number(),
  previousUploadsSuperseded: z.number(),
  unmatched: PurchaseUnmatchedSummarySchema,
  validationIssues: z.array(PurchaseUploadIssueSchema),
});

export type ImportPurchaseFileRequest = z.infer<
  typeof ImportPurchaseFileRequestSchema
>;
export type ImportPurchaseFileResponse = z.infer<
  typeof ImportPurchaseFileResponseSchema
>;

export const ListPurchaseUploadsRequestSchema = z.object({
  year: z.number().int().min(2000).max(2100),
});

export const PurchaseUploadListItemSchema = z.object({
  id: z.string().uuid(),
  fileName: z.string(),
  uploadedAt: z.coerce.date(),
  uploadedByName: z.string(),
  throughMonth: z.number().int().min(1).max(12),
  isPartialMonth: z.boolean(),
  rowCount: z.number(),
  dataMissing: z.boolean(),
  currentYearTotal: z.number().optional(),
  priorYearTotal: z.number().optional(),
  superseded: z.boolean(),
  supersededAt: z.coerce.date().nullable(),
});

export const ListPurchaseUploadsResponseSchema = z.object({
  data: z.array(PurchaseUploadListItemSchema),
});

export type ListPurchaseUploadsRequest = z.infer<
  typeof ListPurchaseUploadsRequestSchema
>;
export type PurchaseUploadListItem = z.infer<
  typeof PurchaseUploadListItemSchema
>;
export type ListPurchaseUploadsResponse = z.infer<
  typeof ListPurchaseUploadsResponseSchema
>;

export const DeletePurchaseUploadRequestSchema = z.object({
  uploadId: z.string().uuid(),
});

export const DeletePurchaseUploadResponseSchema = z.object({
  success: z.boolean(),
});

export type DeletePurchaseUploadRequest = z.infer<
  typeof DeletePurchaseUploadRequestSchema
>;
export type DeletePurchaseUploadResponse = z.infer<
  typeof DeletePurchaseUploadResponseSchema
>;

export const purchaseUploadContracts = {
  parse: oc
    .input(ParsePurchaseFileRequestSchema)
    .output(ParsePurchaseFileResponseSchema)
    .route({
      method: "POST",
      summary: "Preview a rebate supplier purchases file",
      description:
        "Parses an uploaded YTD purchases workbook and returns row issues, checksum warnings, totals, inferred through-month, and supplier matching. Saves nothing.",
    }),

  import: oc
    .input(ImportPurchaseFileRequestSchema)
    .output(ImportPurchaseFileResponseSchema)
    .route({
      method: "POST",
      summary: "Import rebate supplier purchases for a year",
      description:
        "Stores the uploaded YTD snapshot for the organization and year. Previous uploads for that year are marked superseded and their rows are kept.",
    }),

  list: oc
    .input(ListPurchaseUploadsRequestSchema)
    .output(ListPurchaseUploadsResponseSchema)
    .route({
      method: "GET",
      summary: "List rebate supplier purchase uploads for a year",
      description:
        "Returns the organization's purchase uploads for a year, newest first. Each item includes file name, uploaded at, uploaded by, through-month, totals, and supersededAt as display metadata when a later file replaced it.",
    }),

  delete: oc
    .input(DeletePurchaseUploadRequestSchema)
    .output(DeletePurchaseUploadResponseSchema)
    .route({
      method: "POST",
      summary: "Delete a rebate supplier purchase upload",
      description:
        "Deletes one purchase upload and its stored purchase rows for the organization. Agreements, statement line items, and calculations are not changed.",
    }),
} as const;
